/**
 * EnvironmentSelector Component
 *
 * Displays a dropdown/toggle for choosing between sandbox and production environments.
 * Clearly indicates the current environment selection and handles environment changes.
 * Persists the selection in component state during the session.
 *
 * Requirements:
 * - 12.1: Provide selection control for choosing between "sandbox" and "production"
 * - 12.2: Route requests to sandbox gateway when sandbox is selected
 * - 12.3: Route requests to production gateway when production is selected
 * - 12.4: Clearly indicate which environment is currently selected
 * - 12.5: Persist environment selection in component state during session
 */

import { useState, useEffect } from 'react';

export interface EnvironmentSelectorProps {
  /** Currently selected environment */
  selectedEnvironment: 'sandbox' | 'production';
  /** Callback when environment selection changes */
  onEnvironmentChange: (environment: 'sandbox' | 'production') => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

/**
 * EnvironmentSelector component displays a control for choosing between
 * sandbox and production environments.
 *
 * @param selectedEnvironment - Currently selected environment
 * @param onEnvironmentChange - Callback function when selection changes
 * @param disabled - Whether the selector is disabled
 */
export const EnvironmentSelector = ({
  selectedEnvironment,
  onEnvironmentChange,
  disabled = false,
}: EnvironmentSelectorProps) => {
  // Persist selection in component state (Requirement 12.5)
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>(selectedEnvironment);

  /**
   * Sync internal state with prop changes
   */
  useEffect(() => {
    setEnvironment(selectedEnvironment);
  }, [selectedEnvironment]);

  /**
   * Handle environment selection change
   */
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newEnvironment = event.target.value as 'sandbox' | 'production';
    setEnvironment(newEnvironment);
    onEnvironmentChange(newEnvironment);
  };

  /**
   * Get environment badge color and icon
   */
  const getEnvironmentStyle = (env: 'sandbox' | 'production') => {
    if (env === 'sandbox') {
      return {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        icon: '🧪',
        label: 'Sandbox',
        description: 'Test environment - safe for experimentation',
      };
    }
    return {
      color: 'bg-green-100 text-green-800 border-green-300',
      icon: '🚀',
      label: 'Production',
      description: 'Live environment - affects real data',
    };
  };

  const currentStyle = getEnvironmentStyle(environment);

  return (
    <div className="space-y-2">
      <label htmlFor="environment-selector" className="block text-sm font-medium text-gray-700">
        Environment
        <span className="text-red-500 ml-1" aria-label="required">
          *
        </span>
      </label>

      {/* Environment dropdown selector (Requirements 12.1, 12.4) */}
      <select
        id="environment-selector"
        value={environment}
        onChange={handleChange}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        aria-label="Select environment"
      >
        <option value="sandbox">🧪 Sandbox - Test Environment</option>
        <option value="production">🚀 Production - Live Environment</option>
      </select>

      {/* Current environment indicator (Requirement 12.4) */}
      <div
        className={`p-3 border rounded-md ${currentStyle.color}`}
        role="status"
        aria-live="polite"
      >
        <div className="flex items-start gap-2">
          <span className="text-lg" aria-hidden="true">
            {currentStyle.icon}
          </span>
          <div className="flex-1">
            <p className="text-sm font-semibold">{currentStyle.label} Environment Selected</p>
            <p className="text-xs mt-1">{currentStyle.description}</p>
          </div>
        </div>
      </div>

      {/* Warning for production environment */}
      {environment === 'production' && (
        <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
          <div className="flex items-start gap-2">
            <span className="text-orange-600 text-lg" aria-hidden="true">
              ⚠️
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800">Production Environment Warning</p>
              <p className="text-xs text-orange-700 mt-1">
                You are testing against the live production environment. Any requests sent will
                affect real data. Use with caution.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info for sandbox environment */}
      {environment === 'sandbox' && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="flex items-start gap-2">
            <span className="text-blue-600 text-lg" aria-hidden="true">
              ℹ️
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800">Sandbox Environment</p>
              <p className="text-xs text-blue-700 mt-1">
                This is a safe testing environment. Feel free to experiment without affecting
                production data.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvironmentSelector;
